"use client";

import React from "react";
import { Compass, FlaskConical, Building2, Landmark, ArrowRight, UserCheck } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const affiliatedUnits = [{
  id: "unit-1",
  role: "Chủ tịch",
  leader: "Đỗ Đức Bình",
  name: "Phân hội Khoa học Địa lý Phong thủy Việt Nam",
  iconName: "compass",
  description: "Nghiên cứu, chuẩn hóa và ứng dụng các nguyên lý địa lý phong thủy trong quy hoạch không gian sinh thái và bảo vệ cảnh quan."
}, {
  id: "unit-2",
  role: "Giám đốc",
  leader: "TS. Đỗ Ngọc Chung",
  name: "Trung tâm Khoa học công nghệ Địa lý Môi trường Việt Nam (CEGTES)",
  iconName: "flask",
  description: "Triển khai các đề tài nghiên cứu khoa học, chuyển giao công nghệ phân tích địa lý và đánh giá tác động môi trường thực địa."
}, {
  id: "unit-3",
  role: "Đại diện phụ trách",
  leader: "Phạm Hồng Hải",
  name: "Viện Địa lý môi trường (IEG)",
  iconName: "building",
  description: "Tổ chức khảo sát chuyên sâu địa lý tự nhiên, biến đổi khí hậu và quy hoạch tài nguyên bền vững phục vụ chiến lược quốc gia."
}, {
  id: "unit-4",
  role: "Đại diện phụ trách",
  leader: "Nguyễn Thị Tuyết Mai",
  name: "Trung tâm nghiên cứu giá trị văn hóa - tôn giáo vì môi trường phát triển bền vững",
  iconName: "landmark",
  description: "Bảo tồn di sản địa văn hóa và phát huy các giá trị tín ngưỡng bản địa gắn liền với bảo vệ tài nguyên thiên nhiên."
}];
export default function Units() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0.1 : 0.38,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  const renderIcon = (type: string) => {
    switch (type) {
      case "compass":
        return <Compass className="size-5 text-primary" strokeWidth={1.75} />;
      case "flask":
        return <FlaskConical className="size-5 text-primary" strokeWidth={1.75} />;
      case "building":
        return <Building2 className="size-5 text-primary" strokeWidth={1.75} />;
      case "landmark":
        return <Landmark className="size-5 text-primary" strokeWidth={1.75} />;
      default:
        return <Building2 className="size-5 text-primary" strokeWidth={1.75} />;
    }
  };
  return <section id="các-đơn-vị-trực-thuộc-nổi-bật" data-nav="light" className="relative bg-background py-16 md:py-20 lg:py-24 border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-12 md:mb-16">
          <div className="flex items-center gap-2 mb-3">
            <span className="h-2.5 w-2.5 rounded-full bg-primary" />
            <span className="text-xs md:text-sm font-semibold tracking-wider text-muted-foreground uppercase">
              Cơ cấu tổ chức chuyên môn
            </span>
          </div>

          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div className="max-w-3xl">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold font-default text-secondary tracking-tight leading-tight">
                Các đơn vị trực thuộc nổi bật
              </h2>
              {/* Institutional dual hairline divider */}
              <div className="mt-4 flex items-center">
                <span className="h-0.5 w-12 bg-secondary" />
                <span className="h-px flex-1 bg-border" />
              </div>
            </div>

            <div className="shrink-0">
              <Button asChild variant="outline" className="rounded-xs border-primary text-primary hover:bg-accent hover:text-primary transition-[color,background-color,border-color,box-shadow]">
                <Link to="/OrganizationStructurePage" className="inline-flex items-center gap-2">
                  <span>Xem toàn bộ sơ đồ tổ chức</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* 4-column responsive grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {affiliatedUnits.map((unit, i) => <motion.div key={unit.id} data-index={i} variants={cardVariants} className="group flex flex-col justify-between bg-card rounded-xs border border-border border-t-[3px] border-t-primary p-6 shadow-xs hover:shadow-md hover:border-primary/60 transition-[border-color,box-shadow] duration-200">
              <div>
                {/* Icon Container */}
                <div className="size-11 rounded-xs bg-accent flex items-center justify-center mb-6">
                  {renderIcon(unit.iconName)}
                </div>

                {/* Unit Name */}
                <h3 className="text-base md:text-lg font-semibold font-default text-foreground leading-snug line-clamp-3 min-h-[4rem] mb-3">
                  {unit.name}
                </h3>

                {/* Director / Presiding Leader */}
                <div className="flex items-center gap-2 py-2 px-2.5 rounded-xs bg-muted/60 border border-border/70 mb-4">
                  <UserCheck className="size-4 text-muted-foreground shrink-0" strokeWidth={1.75} />
                  <div className="text-xs text-foreground truncate">
                    <span className="text-muted-foreground font-medium">{unit.role}: </span>
                    <span className="font-semibold">{unit.leader}</span>
                  </div>
                </div>

                {/* Unit Brief Description */}
                <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3 mb-6">
                  {unit.description}
                </p>
              </div>

              {/* Action Button */}
              <div className="pt-4 border-t border-border mt-auto">
                <Button asChild variant="outline" size="sm" className="w-full rounded-xs border-primary/50 text-primary hover:bg-accent hover:text-primary transition-[color,background-color,border-color]">
                  <Link to="/OrganizationStructurePage" className="inline-flex items-center justify-center gap-1.5 font-medium text-xs">
                    <span>Xem chi tiết</span>
                    <ArrowRight className="size-3.5 group-hover:translate-x-0.5 transition-transform" />
                  </Link>
                </Button>
              </div>
            </motion.div>)}
        </motion.div>
      </div>
    </section>;
}